import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JobStageIssueService, ResolveJobIssueRequest } from '../../../../core/services/job-stage-issue.service';
import { JobStageIssueResponse } from '../../../../core/models/job-stage-issue.models';
import { NotificationStateService } from '../../../../core/services/notification-state.service';

@Component({
  selector: 'app-resolve-job-issue-dialog',
  template: `
    <div class="resolve-dialog">
      <h2 mat-dialog-title>Resolve Job Issue</h2>

      <mat-dialog-content>
        <div class="issue-info">
          <h3>Issue Details</h3>
          <div class="info-row">
            <strong>Job Item:</strong> {{ data.jobItemName }}
          </div>
          <div class="info-row">
            <strong>Serial Number:</strong> {{ data.serialNumber }}
          </div>
          <div class="info-row">
            <strong>Issue:</strong> {{ data.issueDescription }}
          </div>
        </div>

        <form [formGroup]="resolveForm" class="resolve-form">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Resolution Notes</mat-label>
            <textarea
              matInput
              formControlName="resolutionNotes"
              placeholder="Describe how the issue was resolved..."
              rows="4"
            ></textarea>
            <mat-error *ngIf="resolveForm.get('resolutionNotes')?.hasError('required') && resolveForm.get('resolutionNotes')?.touched">
              Resolution notes are required
            </mat-error>
          </mat-form-field>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button
          mat-button
          (click)="cancel()"
          [disabled]="isLoading"
        >
          Cancel
        </button>
        <button
          mat-raised-button
          color="primary"
          (click)="resolve()"
          [disabled]="resolveForm.invalid || isLoading"
        >
          <mat-icon *ngIf="isLoading">hourglass_empty</mat-icon>
          {{ isLoading ? 'Resolving...' : 'Resolve Issue' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .resolve-dialog {
        min-width: 500px;
      }

      .issue-info {
        background-color: #f5f5f5;
        padding: 16px;
        border-radius: 8px;
        margin-bottom: 24px;
      }

      .issue-info h3 {
        margin: 0 0 16px 0;
        color: #1a237e;
        font-size: 16px;
        font-weight: 500;
      }

      .info-row {
        margin-bottom: 8px;
        line-height: 1.4;
      }

      .info-row strong {
        color: #333;
        margin-right: 8px;
      }

      .resolve-form {
        display: flex;
        flex-direction: column;
      }

      .full-width {
        width: 100%;
      }

      mat-dialog-actions {
        padding: 16px 24px 24px;
      }

      button mat-icon {
        margin-right: 8px;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `,
  ],
})
export class ResolveJobIssueDialogComponent {
  resolveForm: FormGroup;
  isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<ResolveJobIssueDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: JobStageIssueResponse,
    private fb: FormBuilder,
    private jobStageIssueService: JobStageIssueService,
    private notificationStateService: NotificationStateService
  ) {
    this.resolveForm = this.fb.group({
      resolutionNotes: ['', Validators.required]
    });
  }

  resolve() {
    if (this.resolveForm.invalid) {
      this.resolveForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const request: ResolveJobIssueRequest = {
      resolutionNotes: this.resolveForm.value.resolutionNotes
    };

    this.jobStageIssueService.resolveJobIssue(this.data.id, request).subscribe({
      next: () => {
        this.isLoading = false;
        // Notify listeners first (e.g., job grid) that the issue was resolved for this serial
        // This will trigger the notification dialog to refresh
        this.notificationStateService.notifyIssueResolvedBySerial(this.data.serialNumber);
        // Refresh notifications to update the list - wait for it to complete
        this.notificationStateService.refreshIssues().subscribe({
          next: () => {
            // Refresh completed, close dialog
            this.dialogRef.close(true);
          },
          error: () => {
            // Even if refresh fails, close the dialog since resolution succeeded
            this.dialogRef.close(true);
          }
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error resolving job issue:', error);
        const message = typeof error?.error === 'string' && error.error ? error.error : 'Failed to resolve job issue';
        // Try to show a Material snackbar if available in app
        try { (window as any).snackBar?.open?.(message, 'Close', { duration: 3000 }); } catch { alert(message); }
      }
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
} 